(function () {
  'use strict';

  var CONFIG_ID = 'lpl-contextual-popup-config';
  var ROOT_ID = 'lpl-contextual-popup-root';
  var root;
  var config;
  var context;
  var lastFocused;
  var submissionId;
  var visitorId;
  var impressionId;
  var activeExperiment;
  var activeVariant;
  var activeCopyExperiment;
  var activeCopyVariant;
  var triggerReason = '';
  var pendingStyleOpenReason = null;
  var waitingForPopupStyles = false;
  var triggerCleanups = [];
  var responsiveImageCleanups = [];
  var eventCounter = 0;
  var identityStatus = 'not_checked';
  var identityEligibilityRevoked = false;
  var lateIdentitySuppressionEmitted = false;
  var captureCompleted = false;
  var submittedEventEmitted = false;
  var contextImageUrls = { desktop: '', mobile: '' };
  var activeContextImageUrl = '';
  var imageSourceAssigned = false;
  var inertBackgroundRecords = [];
  var STORAGE_PREFIX = 'lplcp';
  var IMAGE_PRELOAD_LEAD_MS = 4000;

  function parseConfig() {
    var node = document.getElementById(CONFIG_ID);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '');
    } catch (_error) {
      return null;
    }
  }

  function normalizedPathname() {
    var path = window.location.pathname || '/';
    return path.length > 1 ? path.replace(/\/$/, '') : path;
  }

  function contextSurface() {
    var surface = String((context && context.surface) || 'editorial');
    return surface === 'product' ? 'product' : surface === 'non_product' ? 'non_product' : 'editorial';
  }

  function frequencySurface() {
    return contextSurface() === 'product' ? 'product' : 'editorial';
  }

  function queryMatches(name, value) {
    return new URLSearchParams(window.location.search).get(name) === value;
  }

  function isReviewMode() {
    return queryMatches(config.pilot.review_param, config.pilot.review_value);
  }

  function readStorage(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (_error) {
      return false;
    }
  }

  function removeStorage(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (_error) {
      // Storage may be unavailable in privacy-restricted browsers.
    }
  }

  function readSessionStorage(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  function writeSessionStorage(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
      return true;
    } catch (_error) {
      return false;
    }
  }

  function createRandomId(prefix) {
    var random = window.crypto && typeof window.crypto.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
    return prefix + '-' + random;
  }

  function getVisitorId() {
    if (visitorId) return visitorId;
    var key = STORAGE_PREFIX + ':visitor_id';
    visitorId = readStorage(key) || createRandomId('visitor');
    writeStorage(key, visitorId);
    return visitorId;
  }

  function hashString(value) {
    var hash = 2166136261;
    for (var index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  }

  function validVariants(experiment) {
    if (!experiment || !Array.isArray(experiment.variants)) return [];
    return experiment.variants.filter(function (variant) {
      return variant && variant.id && Number(variant.weight) > 0;
    });
  }

  function chooseVariant(experiment) {
    var variants = validVariants(experiment);
    if (!variants.length) return null;

    var requestedId = isReviewMode()
      ? new URLSearchParams(window.location.search).get(config.pilot.variant_param)
      : null;
    var requested = variants.find(function (variant) { return variant.id === requestedId; });
    if (requested) return requested;

    var assignmentKey = STORAGE_PREFIX + ':assignment:' + experiment.id + ':v' +
      String(experiment.version || 1);
    var storedId = readStorage(assignmentKey);
    var stored = variants.find(function (variant) { return variant.id === storedId; });
    if (stored) return stored;

    var totalWeight = variants.reduce(function (sum, variant) {
      return sum + Number(variant.weight);
    }, 0);
    var seed = experiment.id + ':v' + String(experiment.version || 1) + ':' + getVisitorId();
    var point = (hashString(seed) / 4294967296) * totalWeight;
    var cumulative = 0;
    var selected = variants[variants.length - 1];
    variants.some(function (variant) {
      cumulative += Number(variant.weight);
      if (point < cumulative) {
        selected = variant;
        return true;
      }
      return false;
    });
    writeStorage(assignmentKey, selected.id);
    return selected;
  }

  function chooseCopyVariant(experiment) {
    var variants = validVariants(experiment);
    if (!variants.length) return null;

    var current = variants.find(function (variant) { return variant.id === 'current'; }) || variants[0];
    if (experiment.status !== 'running' && !isReviewMode()) return current;

    var requestedId = isReviewMode()
      ? new URLSearchParams(window.location.search).get(
        experiment.review_variant_param || 'lpl_popup_copy_variant'
      )
      : null;
    var requested = variants.find(function (variant) { return variant.id === requestedId; });
    if (requested) return requested;

    var assignmentKey = STORAGE_PREFIX + ':assignment:' + experiment.id + ':v' +
      String(experiment.version || 1);
    var storedId = readStorage(assignmentKey);
    var stored = variants.find(function (variant) { return variant.id === storedId; });
    if (stored) return stored;

    var totalWeight = variants.reduce(function (sum, variant) {
      return sum + Number(variant.weight);
    }, 0);
    var assignmentSalt = String(experiment.assignment_salt || experiment.id);
    var seed = assignmentSalt + ':' + experiment.id + ':v' +
      String(experiment.version || 1) + ':' + getVisitorId();
    var point = (hashString(seed) / 4294967296) * totalWeight;
    var cumulative = 0;
    var selected = variants[variants.length - 1];
    variants.some(function (variant) {
      cumulative += Number(variant.weight);
      if (point < cumulative) {
        selected = variant;
        return true;
      }
      return false;
    });
    writeStorage(assignmentKey, selected.id);
    return selected;
  }

  function applyVariants() {
    activeExperiment = context.experiment || null;
    activeVariant = chooseVariant(activeExperiment);
    if (activeVariant && activeVariant.overrides) {
      var overrides = activeVariant.overrides;
      context.content = Object.assign({}, context.content, overrides.content || {});
      context.trigger = Object.assign({}, context.trigger, overrides.trigger || {});
      context.presentation = Object.assign({}, context.presentation || {}, overrides.presentation || {});
    }

    activeCopyExperiment = context.copy_experiment || null;
    activeCopyVariant = chooseCopyVariant(activeCopyExperiment);
    if (!activeCopyVariant || !activeCopyVariant.content) return;
    var allowedCopyOverrides = {};
    ['eyebrow', 'headline', 'body'].forEach(function (field) {
      if (Object.prototype.hasOwnProperty.call(activeCopyVariant.content, field)) {
        allowedCopyOverrides[field] = activeCopyVariant.content[field];
      }
    });
    context.content = Object.assign({}, context.content, allowedCopyOverrides);
  }

  function safeProductUrl(value) {
    try {
      var url = new URL(value || '/', window.location.origin);
      if (url.origin !== window.location.origin) return '/';
      url.searchParams.set('utm_source', 'onsite');
      url.searchParams.set('utm_medium', 'contextual_popup');
      url.searchParams.set('utm_campaign', experimentId());
      url.searchParams.set('utm_content', variantId());
      return url.pathname + url.search + url.hash;
    } catch (_error) {
      return '/';
    }
  }

  function safeDiscountUrl(code, destination) {
    var normalizedCode = String(code || '').trim();
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(normalizedCode)) {
      return safeProductUrl(destination);
    }
    var redirect = safeProductUrl(destination);
    return '/discount/' + encodeURIComponent(normalizedCode) +
      '?redirect=' + encodeURIComponent(redirect);
  }

  function offerLabel() {
    var value = context.commerce.offer_value;
    if (context.commerce.offer_type === 'percentage') return String(value) + '% Off';
    if (context.commerce.offer_type === 'fixed_amount') return '$' + String(value) + ' Off';
    return String(value || 'Welcome offer');
  }

  function setText(key, value) {
    root.querySelectorAll('[data-lplcp-text="' + key + '"]').forEach(function (node) {
      node.textContent = value == null ? '' : String(value);
    });
  }

  function configuredAssetUrl(key) {
    var normalizedKey = String(key || '').trim();
    if (!normalizedKey || !config) return '';
    var primary = config.assets && config.assets[normalizedKey];
    var fallback = config.image_catalog && config.image_catalog[normalizedKey];
    return String(primary || fallback || '');
  }

  function makeBackgroundInert() {
    if (!document.body || inertBackgroundRecords.length) return;
    inertBackgroundRecords = Array.from(document.body.children || []).filter(function (node) {
      return node !== root && !node.contains(root);
    }).map(function (node) {
      var record = { node: node, hadInert: node.hasAttribute('inert') };
      if (!record.hadInert) node.setAttribute('inert', '');
      return record;
    });
  }

  function restoreBackgroundInteractivity() {
    inertBackgroundRecords.forEach(function (record) {
      if (record.node && !record.hadInert) record.node.removeAttribute('inert');
    });
    inertBackgroundRecords = [];
  }

  function restorePreviousFocus() {
    if (!lastFocused || !lastFocused.isConnected || typeof lastFocused.focus !== 'function') return;
    try {
      lastFocused.focus({ preventScroll: true });
    } catch (_error) {
      lastFocused.focus();
    }
  }

  function timingExperimentId() {
    return activeExperiment && activeExperiment.id
      ? activeExperiment.id
      : context.attribution.experiment;
  }

  function timingExperimentVersion() {
    return activeExperiment && activeExperiment.version
      ? String(activeExperiment.version)
      : '1';
  }

  function factorialReportingIsActive() {
    var factorial = config.factorial_experiment || {};
    return Boolean(factorial.id && activeCopyExperiment && activeCopyVariant &&
      (activeCopyExperiment.status === 'running' || isReviewMode()) &&
      factorial.timing_experiment_id === timingExperimentId() &&
      factorial.copy_experiment_id === copyExperimentId());
  }

  function experimentId() {
    return factorialReportingIsActive() ? factorialExperimentId() : timingExperimentId();
  }

  function experimentVersion() {
    return factorialReportingIsActive()
      ? factorialExperimentVersion()
      : timingExperimentVersion();
  }

  function deviceType() {
    if (typeof window.matchMedia === 'function') {
      return window.matchMedia('(max-width: 720px)').matches ? 'mobile' : 'desktop';
    }
    return window.innerWidth <= 720 ? 'mobile' : 'desktop';
  }

  function variantId() {
    return factorialReportingIsActive() ? factorialCellId() : timingArmId();
  }

  function timingArmId() {
    return activeVariant && activeVariant.id
      ? activeVariant.id
      : context.attribution.variant;
  }

  function copyExperimentId() {
    return activeCopyExperiment && activeCopyExperiment.id
      ? activeCopyExperiment.id
      : '';
  }

  function copyExperimentVersion() {
    return activeCopyExperiment && activeCopyExperiment.version
      ? String(activeCopyExperiment.version)
      : '';
  }

  function copyArmId() {
    return activeCopyVariant && activeCopyVariant.id
      ? activeCopyVariant.id
      : 'current';
  }

  function factorialExperimentId() {
    var factorial = config.factorial_experiment || {};
    return String(factorial.id || '');
  }

  function factorialExperimentVersion() {
    var factorial = config.factorial_experiment || {};
    return factorial.version ? String(factorial.version) : '';
  }

  function factorialCellId() {
    var factorial = config.factorial_experiment || {};
    var cells = Array.isArray(factorial.cells) ? factorial.cells : [];
    var timingArm = timingArmId();
    var copyArm = copyArmId();
    var matched = cells.find(function (cell) {
      return cell && cell.timing_arm === timingArm && cell.copy_arm === copyArm;
    });
    return matched && matched.id ? String(matched.id) : timingArm + '__' + copyArm;
  }

  function localEventKey() {
    return STORAGE_PREFIX + ':events:v1';
  }

  function localEvents() {
    try {
      var value = JSON.parse(readStorage(localEventKey()) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (_error) {
      return [];
    }
  }

  function persistLocalEvent(detail) {
    var analytics = config.analytics || {};
    if (!isReviewMode() || analytics.local_log_in_review !== true) return;
    var events = localEvents();
    events.push(detail);
    var limit = Math.max(1, Number(analytics.max_local_events || 500));
    writeStorage(localEventKey(), JSON.stringify(events.slice(-limit)));
  }

  function publishExternalEvent(detail) {
    var analytics = config.analytics || {};
    if (isReviewMode() && analytics.external_in_review !== true) return;
    var name = analytics.event_name || 'lpl_contextual_popup';
    // Keep GA4 below its 25-parameter limit. The composite experiment and
    // cell identify the test; full attribution remains in the local detail.
    var analyticsValues = {
      popup_action: detail.action,
      popup_context_id: detail.context_id,
      popup_product_lane: detail.product_lane,
      popup_offer_type: detail.offer_type,
      popup_surface: detail.surface,
      popup_identity_status: detail.identity_status,
      popup_experiment_id: detail.experiment_id,
      popup_experiment_version: detail.experiment_version,
      popup_variant_id: detail.variant_id,
      popup_timing_arm: detail.timing_arm,
      popup_copy_arm: detail.copy_arm,
      popup_impression_id: detail.impression_id,
      popup_trigger_reason: detail.trigger_reason,
      popup_format: detail.format,
      popup_image_type: detail.image_type,
      popup_transport: detail.transport,
      popup_suppression_reason: detail.suppression_reason || '',
      popup_submission_state: detail.submission_state || '',
      popup_failure_stage: detail.failure_stage || '',
      popup_error_class: detail.error_class || '',
      popup_status_class: detail.status_class || '',
      popup_telemetry_schema: 'compact_v2'
    };
    try {
      // Queue the exact gtag command shape even when gtag.js has not loaded yet.
      // A GTM-style `{event: ...}` object is not a safe substitute for the
      // arguments queue consumed by gtag.js. Use one GA4 sink so a Shopify
      // custom-event subscriber cannot duplicate the same measurement.
      var dataLayer = window.dataLayer = window.dataLayer || [];
      var send = typeof window.gtag === 'function'
        ? window.gtag
        : function () { dataLayer.push(arguments); };
      var destination = String(analytics.measurement_id || '').trim();
      var eventValues = Object.assign({}, analyticsValues, {
        transport_type: 'beacon'
      });
      if (/^G-[A-Z0-9]+$/.test(destination)) {
        eventValues.send_to = destination;
      }
      send('event', name, eventValues);
    } catch (_error) {
      // Analytics must never interrupt form display or submission.
    }
  }

  function emit(action, extra) {
    var detail = Object.assign({
      event_id: impressionId + ':' + action + ':' + String(++eventCounter),
      occurred_at: new Date().toISOString(),
      action: action,
      visitor_id: getVisitorId(),
      impression_id: impressionId,
      context_id: context.context_id,
      pathname: normalizedPathname(),
      product_lane: context.attribution.product_lane,
      offer_type: context.commerce.offer_type,
      surface: contextSurface(),
      identity_status: identityStatus,
      experiment_id: experimentId(),
      experiment_version: experimentVersion(),
      variant_id: variantId(),
      timing_experiment_id: timingExperimentId(),
      timing_experiment_version: timingExperimentVersion(),
      timing_arm: timingArmId(),
      copy_experiment_id: copyExperimentId(),
      copy_experiment_version: copyExperimentVersion(),
      copy_arm: copyArmId(),
      factorial_experiment_id: factorialExperimentId(),
      factorial_experiment_version: factorialExperimentVersion(),
      factorial_cell_id: factorialCellId(),
      experiment_status: factorialReportingIsActive()
        ? String((config.factorial_experiment || {}).status || '')
        : (activeExperiment ? activeExperiment.status : ''),
      trigger_reason: triggerReason,
      format: (context.presentation && context.presentation.format) || 'modal',
      image_type: (context.presentation && context.presentation.image_type) || 'contextual',
      device_type: deviceType(),
      transport: isLiveTransport() ? 'live' : 'mock'
    }, extra || {});
    persistLocalEvent(detail);
    publishExternalEvent(detail);
    try {
      window.dispatchEvent(new CustomEvent('lpl:contextual-popup', { detail: detail }));
    } catch (_error) {
      // Local observers are diagnostic only and can never break the customer flow.
    }
  }

  function emitSubmittedOnce(extra) {
    if (submittedEventEmitted) return;
    submittedEventEmitted = true;
    emit('submitted', extra);
  }

  function isLiveTransport() {
    if (isReviewMode()) {
      return queryMatches(
        config.pilot.live_transport_param,
        config.pilot.live_transport_value
      );
    }
    return config.pilot.production_enabled === true &&
      config.pilot.transport_default === 'live';
  }

  function productionIsEnabled() {
    var experimentCanRun = !activeExperiment || activeExperiment.status === 'running';
    return config.pilot.production_enabled === true && experimentCanRun;
  }

  function submittedStorageKey() {
    return STORAGE_PREFIX + ':submitted:v1';
  }

  function dismissalStorageKey(surface) {
    return STORAGE_PREFIX + ':dismissed_until:' + surface + ':v2';
  }

  function opportunityCountStorageKey() {
    return STORAGE_PREFIX + ':session_opportunities:v2';
  }

  function openedSurfaceStorageKey(surface) {
    return STORAGE_PREFIX + ':opened_session:' + surface + ':v2';
  }

  function sessionOpportunityCount() {
    var count = Number(readSessionStorage(opportunityCountStorageKey()));
    return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  }

  function maxSessionOpportunities() {
    var configured = Number((config.suppression || {}).max_session_opportunities || 2);
    return Math.max(1, Math.floor(configured));
  }

  function localSuppressionReason() {
    var storefrontContext = window.LPL_KLAVIYO_CONTEXT || {};
    if (window.Shopify && window.Shopify.designMode) return 'shopify_design_mode';
    if (storefrontContext.customer_logged_in === true) return 'shopify_customer';
    if (readStorage(submittedStorageKey()) === '1') return 'submitted_browser';
    if (sessionOpportunityCount() >= maxSessionOpportunities()) return 'session_opportunity_limit';

    var surface = frequencySurface();
    if (surface === 'product') {
      if (readSessionStorage(openedSurfaceStorageKey('product')) === '1') {
        return 'product_opened_this_session';
      }
    } else {
      if (readSessionStorage(openedSurfaceStorageKey('product')) === '1') {
        return 'product_precedence_this_session';
      }
      if (readSessionStorage(openedSurfaceStorageKey('editorial')) === '1') {
        return 'editorial_opened_this_session';
      }
    }

    var dismissedUntil = Number(readStorage(dismissalStorageKey(surface)));
    if (Number.isFinite(dismissedUntil) && dismissedUntil > Date.now()) {
      return surface + '_dismissal_cooldown';
    }
    return '';
  }

  function markOpenedThisSession() {
    if (isReviewMode()) return;
    var surface = frequencySurface();
    writeSessionStorage(opportunityCountStorageKey(), String(sessionOpportunityCount() + 1));
    writeSessionStorage(openedSurfaceStorageKey(surface), '1');
  }

  function markDismissed() {
    if (isReviewMode()) return;
    var surface = frequencySurface();
    var settings = config.suppression || {};
    var days = surface === 'product'
      ? Number(settings.product_dismissal_days || 14)
      : Number(settings.editorial_dismissal_days || 7);
    var duration = Math.max(1, days) * 24 * 60 * 60 * 1000;
    writeStorage(dismissalStorageKey(surface), String(Date.now() + duration));
  }

  function markSubmitted() {
    writeStorage(submittedStorageKey(), '1');
  }

  function identifyBrowser(email) {
    var configuredTimeout = Number((config.klaviyo || {}).identify_timeout_ms || 1200);
    var timeoutMs = Math.max(250, Math.min(2500,
      Number.isFinite(configuredTimeout) ? configuredTimeout : 1200));

    return new Promise(function (resolve) {
      var settled = false;
      var timer;

      function finish(status) {
        if (settled) return;
        settled = true;
        if (timer) window.clearTimeout(timer);
        resolve(status);
      }

      try {
        if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
          // Klaviyo's native onsite request is the supported browser-side path
          // for IP-based profile geolocation. Bound the wait so tracking can
          // never hold an already-accepted subscription or its success UI.
          timer = window.setTimeout(function () {
            finish('native_identify_timeout');
          }, timeoutMs);
          Promise.resolve(window.klaviyo.identify({ email: email })).then(function () {
            finish('native_identify_resolved');
          }).catch(function () {
            finish('native_identify_rejected');
          });
          return;
        }
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(['identify', { email: email }]);
        finish('onsite_queue_enqueued');
      } catch (_error) {
        // The durable first-party submitted flag already prevents another offer.
        finish('onsite_identify_unavailable');
      }
    });
  }

  function suppressForLateIdentity() {
    if (identityEligibilityRevoked || captureCompleted) return;
    identityStatus = 'identified_late';
    identityEligibilityRevoked = true;
    clearTriggerHandlers();
    if (root && !root.hidden) {
      root.classList.remove('is-open');
      root.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('lplcp-is-open');
      root.hidden = true;
      restoreBackgroundInteractivity();
      restorePreviousFocus();
    }
    if (impressionId && !lateIdentitySuppressionEmitted) {
      lateIdentitySuppressionEmitted = true;
      emit('suppressed', { suppression_reason: 'klaviyo_identified_profile_late' });
    }
  }

  function klaviyoIdentityStatus(onLateIdentified) {
    var settings = config.suppression || {};
    var timeoutMs = Math.max(250, Number(settings.identity_timeout_ms || 2500));
    return new Promise(function (resolve) {
      var settled = false;
      var timer = window.setTimeout(function () { finish(null); }, timeoutMs);

      function finish(value) {
        var normalized = value === true ? true : value === false ? false : null;
        if (settled) {
          if (normalized === true && typeof onLateIdentified === 'function') {
            onLateIdentified();
          }
          return;
        }
        settled = true;
        window.clearTimeout(timer);
        resolve(normalized);
      }

      try {
        if (window.klaviyo && typeof window.klaviyo.isIdentified === 'function') {
          Promise.resolve(window.klaviyo.isIdentified()).then(finish).catch(function () {
            finish(null);
          });
          return;
        }
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(['isIdentified', finish]);
      } catch (_error) {
        finish(null);
      }
    });
  }

  async function suppressionDecision() {
    if (isReviewMode()) {
      identityStatus = 'review_bypass';
      return { allowed: true, reason: 'review_bypass' };
    }

    var localReason = localSuppressionReason();
    if (localReason) {
      identityStatus = 'not_checked_local_suppression';
      return { allowed: false, reason: localReason };
    }

    identityStatus = 'checking';
    var identified = await klaviyoIdentityStatus(suppressForLateIdentity);
    if (identified === true) {
      identityStatus = 'identified';
      return { allowed: false, reason: 'klaviyo_identified_profile' };
    }
    if (identified === false) {
      identityStatus = 'anonymous';
      return { allowed: true, reason: 'klaviyo_unknown_browser' };
    }

    identityStatus = 'unavailable_allowed';
    var policy = String((config.suppression || {}).identity_unavailable_policy || 'allow_instrumented');
    if (policy === 'suppress') {
      identityStatus = 'unavailable_suppressed';
      return { allowed: false, reason: 'klaviyo_identity_unavailable' };
    }
    return {
      allowed: true,
      reason: 'klaviyo_identity_unavailable_allowed',
      instrumentation: 'klaviyo_identity_unavailable_allowed'
    };
  }

  function eventProperties() {
    return {
      context_id: context.context_id,
      source_path: context.match.pathname,
      source_page_id: String(context.source.page_id || ''),
      source_page_title: context.source.page_title || '',
      source_template: context.source.template_suffix || '',
      page_family: context.attribution.page_family,
      topic: context.attribution.topic,
      species: context.attribution.species,
      mapped_product: context.commerce.product_display_name,
      mapped_product_handle: context.commerce.product_handle,
      product_lane: context.attribution.product_lane,
      product_interest: context.attribution.product_interest || context.attribution.product_lane,
      popup_surface: contextSurface(),
      identity_status: identityStatus,
      offer_type: context.commerce.offer_type,
      offer_value: context.commerce.offer_value,
      offer_currency: context.commerce.currency,
      discount_code: context.commerce.discount_code || '',
      discount_verification_status: context.commerce.discount_verification_status || '',
      experiment: experimentId(),
      experiment_version: experimentVersion(),
      variant: variantId(),
      timing_experiment: timingExperimentId(),
      timing_experiment_version: timingExperimentVersion(),
      timing_arm: timingArmId(),
      copy_experiment: copyExperimentId(),
      copy_experiment_version: copyExperimentVersion(),
      copy_arm: copyArmId(),
      factorial_experiment: factorialExperimentId(),
      factorial_experiment_version: factorialExperimentVersion(),
      factorial_cell_id: factorialCellId(),
      trigger_reason: triggerReason,
      popup_format: (context.presentation && context.presentation.format) || 'modal',
      image_type: (context.presentation && context.presentation.image_type) || 'contextual',
      impression_id: impressionId
    };
  }

  function profileProperties() {
    var values = eventProperties();
    return {
      'LPL Popup Context ID': values.context_id,
      'LPL Landing Page': values.source_path,
      'LPL Landing Topic': values.topic,
      'LPL Species': values.species,
      'LPL Mapped Product': values.mapped_product,
      'LPL Product Lane': values.product_lane,
      product_interest: values.product_interest,
      'LPL Popup Surface': values.popup_surface,
      'LPL Offer': offerLabel(),
      'LPL Discount Code': values.discount_code,
      'LPL Popup Experiment': values.experiment,
      'LPL Popup Experiment Version': values.experiment_version,
      'LPL Popup Variant': values.variant,
      'LPL Popup Timing Experiment': values.timing_experiment,
      'LPL Popup Timing Experiment Version': values.timing_experiment_version,
      'LPL Popup Timing Arm': values.timing_arm,
      'LPL Popup Copy Experiment': values.copy_experiment,
      'LPL Popup Copy Experiment Version': values.copy_experiment_version,
      'LPL Popup Copy Arm': values.copy_arm,
      'LPL Popup Factorial Experiment': values.factorial_experiment,
      'LPL Popup Factorial Experiment Version': values.factorial_experiment_version,
      'LPL Popup Factorial Cell': values.factorial_cell_id
    };
  }

  async function klaviyoRequest(endpoint, payload) {
    var url = 'https://a.klaviyo.com/client/' + endpoint +
      '/?company_id=' + encodeURIComponent(config.klaviyo.public_site_id);
    var response = await fetch(url, {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        'content-type': 'application/vnd.api+json',
        revision: config.klaviyo.revision
      },
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      var requestError = new Error('Klaviyo client request was not accepted');
      requestError.name = 'KlaviyoClientRequestError';
      requestError.status = Number(response.status) || 0;
      throw requestError;
    }
    return response.status;
  }

  function submissionFailureDetail(error) {
    var status = Number(error && error.status);
    var statusClass = Number.isFinite(status) && status >= 100
      ? String(Math.floor(status / 100)) + 'xx'
      : 'none';
    var errorClass = 'network_or_runtime_error';
    if (status === 429) {
      errorClass = 'rate_limited';
    } else if (status >= 500) {
      errorClass = 'server_error';
    } else if (status >= 400) {
      errorClass = 'client_error';
    } else if (error && error.name === 'AbortError') {
      errorClass = 'timeout';
    }
    return {
      submission_state: 'failed',
      failure_stage: 'subscription_transport',
      error_class: errorClass,
      status_class: statusClass
    };
  }

  function subscriptionPayload(email) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'LPL Contextual Popup | ' + context.context_id,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: profileProperties(),
                subscriptions: {
                  email: {
                    marketing: { consent: 'SUBSCRIBED' }
                  }
                }
              }
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: config.klaviyo.list_id
            }
          }
        }
      }
    };
  }

  function eventPayload(email, webIdentityStatus) {
    return {
      data: {
        type: 'event',
        attributes: {
          properties: Object.assign({}, eventProperties(), {
            klaviyo_web_identify_status: webIdentityStatus || 'not_attempted'
          }),
          unique_id: submissionId,
          metric: {
            data: {
              type: 'metric',
              attributes: { name: config.klaviyo.submitted_event_name }
            }
          },
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email }
            }
          }
        }
      }
    };
  }

  function createSubmissionId() {
    if (submissionId) return submissionId;
    submissionId = createRandomId('lplcp-' + context.context_id);
    return submissionId;
  }

  async function submitToKlaviyo(email) {
    createSubmissionId();
    await klaviyoRequest('subscriptions', subscriptionPayload(email));
    markSubmitted();
    captureCompleted = true;
    // The accepted subscription is the conversion boundary. Emit it before
    // optional browser identification, attribution, or success-state UI work.
    emitSubmittedOnce({ submission_state: 'klaviyo_subscription_accepted' });
    var webIdentityStatus = await identifyBrowser(email);
    try {
      await klaviyoRequest('events', eventPayload(email, webIdentityStatus));
    } catch (eventError) {
      console.warn('[LPL contextual popup] subscription succeeded but attribution event failed', eventError.message);
    }
  }

  function showSuccess() {
    captureCompleted = true;
    root.classList.add('lplcp--success-state');
    loadContextImageNow();
    applyContextImagePosition(true);
    root.querySelector('[data-lplcp-capture]').hidden = true;
    root.querySelector('[data-lplcp-success]').hidden = false;
    var dialog = root.querySelector('.lplcp__dialog');
    if (dialog) {
      dialog.setAttribute('aria-labelledby', 'lplcp-success-title');
      dialog.setAttribute('aria-describedby', 'lplcp-success-body');
    }
    root.querySelector('[data-lplcp-product-link]').focus();
    emitSubmittedOnce({ submission_state: isLiveTransport()
      ? 'klaviyo_subscription_accepted'
      : 'mock_success' });
  }

  function legacyCopy(value) {
    var input = document.createElement('textarea');
    input.value = value;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.select();
    var copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (_error) {
      copied = false;
    }
    input.remove();
    return copied;
  }

  async function copyDiscountCode() {
    var code = String(context.commerce.discount_code || '').trim();
    if (!code) return;
    var copied = false;
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(code);
        copied = true;
      } else {
        copied = legacyCopy(code);
      }
    } catch (_error) {
      copied = legacyCopy(code);
    }
    if (!copied) return;
    var button = root.querySelector('[data-lplcp-copy]');
    var status = root.querySelector('[data-lplcp-copy-status]');
    button.textContent = context.content.copied_code_label || 'Copied';
    status.textContent = (context.content.copied_code_label || 'Copied') + ': ' + code;
    emit('code_copied');
    window.setTimeout(function () {
      button.textContent = context.content.copy_code_label || 'Copy code';
    }, 1800);
  }

  function focusableNodes() {
    return Array.from(root.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (node) {
      return !node.hidden && node.offsetParent !== null;
    });
  }

  function clearTriggerHandlers() {
    triggerCleanups.forEach(function (cleanup) { cleanup(); });
    triggerCleanups = [];
  }

  function mediaQueryMatches(query, fallback) {
    if (typeof window.matchMedia === 'function') {
      return window.matchMedia(query).matches;
    }
    return fallback();
  }

  function mobileImageViewport(successState) {
    if (!successState) {
      var breedCapture = (context.presentation || {}).layout_archetype === 'breed_landscape_top';
      var captureMaxWidth = breedCapture ? 947 : 897;
      return mediaQueryMatches('(max-width: ' + captureMaxWidth + 'px)', function () {
        return Number(window.innerWidth) <= captureMaxWidth;
      });
    }
    return mediaQueryMatches(
      '(max-width: 720px), (min-width: 721px) and (max-width: 897px) and (max-height: 650px)',
      function () {
        var width = Number(window.innerWidth);
        var height = Number(window.innerHeight);
        return width <= 720 || (width >= 721 && width <= 897 && height <= 650);
      }
    );
  }

  function contextImageAvailable() {
    return Boolean(contextImageUrls.desktop || contextImageUrls.mobile);
  }

  function selectedContextImage(successState) {
    var mobile = mobileImageViewport(successState);
    if (mobile && contextImageUrls.mobile) {
      return { role: 'mobile', url: contextImageUrls.mobile };
    }
    if (contextImageUrls.desktop) {
      return { role: 'desktop', url: contextImageUrls.desktop };
    }
    return contextImageUrls.mobile
      ? { role: 'mobile', url: contextImageUrls.mobile }
      : { role: '', url: '' };
  }

  function applyContextImagePosition(successState) {
    if (!root) return;
    var image = root.querySelector('[data-lplcp-image]');
    if (!image || !image.style) return;
    var presentation = context.presentation || {};
    var position = mobileImageViewport(successState)
      ? (successState
          ? presentation.mobile_success_object_position
          : presentation.mobile_capture_object_position)
      : presentation.desktop_object_position;
    if (position) image.style.objectPosition = position;
  }

  function loadContextImageNow() {
    if (!contextImageAvailable() || !root) return false;
    var image = root.querySelector('[data-lplcp-image]');
    if (!image) return false;
    var selected = selectedContextImage(captureCompleted);
    if (!selected.url) return false;
    applyContextImagePosition(captureCompleted);
    if (imageSourceAssigned && activeContextImageUrl === selected.url) return false;
    imageSourceAssigned = true;
    activeContextImageUrl = selected.url;
    image.src = selected.url;
    if (image.dataset) image.dataset.lplcpImageRole = selected.role;
    root.dataset.lplcpImageRole = selected.role;
    return true;
  }

  function bindResponsiveImageSelection() {
    if (typeof window.matchMedia !== 'function' || responsiveImageCleanups.length) return;
    [
      '(max-width: 897px)',
      '(max-width: 947px)',
      '(max-width: 720px)',
      '(max-height: 650px)'
    ].forEach(function (query) {
      var media = window.matchMedia(query);
      var refresh = function () {
        if (!imageSourceAssigned && root.hidden) return;
        loadContextImageNow();
      };
      if (typeof media.addEventListener === 'function') {
        media.addEventListener('change', refresh);
        responsiveImageCleanups.push(function () {
          media.removeEventListener('change', refresh);
        });
      } else if (typeof media.addListener === 'function') {
        media.addListener(refresh);
        responsiveImageCleanups.push(function () { media.removeListener(refresh); });
      }
    });
    window.addEventListener('pagehide', function () {
      responsiveImageCleanups.forEach(function (cleanup) { cleanup(); });
      responsiveImageCleanups = [];
    }, { once: true });
  }

  function scheduleProductionImagePreload(delay) {
    if (!contextImageAvailable() || !Number.isFinite(delay) || delay < 0) return;
    var preloadDelay = Math.max(0, delay - IMAGE_PRELOAD_LEAD_MS);
    if (preloadDelay === 0) {
      loadContextImageNow();
      return;
    }
    var timer = window.setTimeout(loadContextImageNow, preloadDelay);
    triggerCleanups.push(function () { window.clearTimeout(timer); });
  }

  function openPopup(reason) {
    if (!root.hidden || identityEligibilityRevoked) return;
    var stylesheet = document.getElementById('lplcp-stylesheet');
    // A blocked/failed stylesheet must never reveal an unstyled dialog or make
    // the background inert. Older markup without this link keeps its behavior.
    if (stylesheet && stylesheet.dataset.lplcpStyles === 'error') return;
    if (stylesheet && !(stylesheet.sheet && stylesheet.media === 'all')) {
      if (pendingStyleOpenReason === null) pendingStyleOpenReason = reason || 'unknown';
      if (!waitingForPopupStyles) {
        waitingForPopupStyles = true;
        var resume = function () {
          stylesheet.removeEventListener('error', failed);
          waitingForPopupStyles = false;
          stylesheet.media = 'all';
          var pendingReason = pendingStyleOpenReason;
          pendingStyleOpenReason = null;
          if (pendingReason !== null) openPopup(pendingReason);
        };
        var failed = function () {
          stylesheet.removeEventListener('load', resume);
          stylesheet.dataset.lplcpStyles = 'error';
          waitingForPopupStyles = false;
          pendingStyleOpenReason = null;
        };
        stylesheet.addEventListener('load', resume, { once: true });
        stylesheet.addEventListener('error', failed, { once: true });
      }
      return;
    }
    // Never delay the assigned trigger. If an earlier scroll/exit trigger beats
    // the scheduled preload, start the image request immediately before reveal.
    loadContextImageNow();
    triggerReason = reason || triggerReason || 'unknown';
    clearTriggerHandlers();
    markOpenedThisSession();
    lastFocused = document.activeElement;
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    makeBackgroundInert();
    document.body.classList.add('lplcp-is-open');
    window.requestAnimationFrame(function () {
      if (root.hidden || identityEligibilityRevoked) return;
      root.classList.add('is-open');
      var input = root.querySelector('[data-lplcp-email]');
      if (input) input.focus({ preventScroll: true });
    });
    emit('opened');
  }

  function closePopup(reason) {
    if (root.hidden) return;
    markDismissed();
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('lplcp-is-open');
    window.setTimeout(function () {
      root.hidden = true;
      restoreBackgroundInteractivity();
      restorePreviousFocus();
    }, 190);
    emit('closed', { close_reason: reason || 'button' });
  }

  function onKeydown(event) {
    if (root.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closePopup('escape');
      return;
    }
    if (event.key !== 'Tab') return;
    var nodes = focusableNodes();
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function currentScrollPercent() {
    var documentElement = document.documentElement;
    var scrollable = Math.max(0, documentElement.scrollHeight - window.innerHeight);
    if (!scrollable) return 100;
    return Math.max(0, Math.min(100, (window.scrollY / scrollable) * 100));
  }

  function scheduleReviewTrigger() {
    var timer = window.setTimeout(function () {
      openPopup('pilot_delay');
    }, Math.max(0, Number(context.trigger.pilot_delay_ms || 0)));
    triggerCleanups.push(function () { window.clearTimeout(timer); });
  }

  function scheduleProductionTriggers() {
    var delay = Number(context.trigger.production_delay_ms);
    scheduleProductionImagePreload(delay);
    if (Number.isFinite(delay) && delay >= 0) {
      var timer = window.setTimeout(function () { openPopup('delay'); }, delay);
      triggerCleanups.push(function () { window.clearTimeout(timer); });
    }

    var scrollTarget = Number(context.trigger.production_scroll_percent);
    if (Number.isFinite(scrollTarget) && scrollTarget > 0 && scrollTarget <= 100) {
      var onScroll = function () {
        if (currentScrollPercent() >= scrollTarget) openPopup('scroll');
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      triggerCleanups.push(function () { window.removeEventListener('scroll', onScroll); });
    }

    if (context.trigger.production_exit_intent === true) {
      var onMouseOut = function (event) {
        if (!event.relatedTarget && event.clientY <= 10) openPopup('exit_intent');
      };
      document.addEventListener('mouseout', onMouseOut);
      triggerCleanups.push(function () { document.removeEventListener('mouseout', onMouseOut); });
    }
  }

  function registerReviewApi() {
    window.LPLPopupExperiment = {
      getAssignment: function () {
        return {
          context_id: context.context_id,
          experiment_id: experimentId(),
          experiment_version: experimentVersion(),
          variant_id: variantId(),
          timing_experiment_id: timingExperimentId(),
          timing_experiment_version: timingExperimentVersion(),
          timing_arm: timingArmId(),
          copy_experiment_id: copyExperimentId(),
          copy_experiment_version: copyExperimentVersion(),
          copy_arm: copyArmId(),
          factorial_experiment_id: factorialExperimentId(),
          factorial_experiment_version: factorialExperimentVersion(),
          factorial_cell_id: factorialCellId(),
          impression_id: impressionId,
          production_enabled: productionIsEnabled(),
          transport: isLiveTransport() ? 'live' : 'mock'
        };
      },
      getLocalEvents: function () { return localEvents().slice(); },
      clearLocalEvents: function () { removeStorage(localEventKey()); },
      downloadLocalEvents: function () {
        var body = localEvents().map(function (event) { return JSON.stringify(event); }).join('\n');
        var blob = new Blob([body ? body + '\n' : ''], { type: 'application/x-ndjson' });
        var url = URL.createObjectURL(blob);
        var link = document.createElement('a');
        link.href = url;
        link.download = 'lpl-popup-events-' + context.context_id + '.ndjson';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(function () { URL.revokeObjectURL(url); }, 0);
      }
    };
  }

  function populate() {
    Object.keys(context.content).forEach(function (key) {
      setText(key, context.content[key]);
    });
    var presentation = context.presentation || {};
    var format = ['modal', 'fullscreen'].includes(presentation.format)
      ? presentation.format
      : 'modal';
    var layoutArchetype = presentation.layout_archetype === 'breed_landscape_top'
      ? 'breed_landscape_top'
      : 'standard_split_portrait';
    root.classList.add('lplcp--format-' + format);
    if (layoutArchetype === 'breed_landscape_top') {
      root.classList.add('lplcp--landscape-top');
    } else {
      root.classList.remove('lplcp--landscape-top');
    }
    root.dataset.lplcpFormat = format;
    root.dataset.lplcpLayout = layoutArchetype;
    root.dataset.lplcpExperiment = experimentId();
    root.dataset.lplcpVariant = variantId();
    root.dataset.lplcpTimingArm = timingArmId();
    root.dataset.lplcpCopyExperiment = copyExperimentId();
    root.dataset.lplcpCopyArm = copyArmId();
    root.dataset.lplcpFactorialExperiment = factorialExperimentId();
    root.dataset.lplcpFactorialCell = factorialCellId();

    var image = root.querySelector('[data-lplcp-image]');
    var universalImageUrl = presentation.image_url ||
      configuredAssetUrl(presentation.image_key) ||
      configuredAssetUrl(context.commerce.product_image_key) ||
      context.commerce.product_image_url || '';
    // Device-specific derivatives are authoritative. The universal source is
    // only a fallback; otherwise a legacy product_image_key can silently make
    // both responsive roles load the same portrait asset.
    var desktopImageUrl = presentation.desktop_image_url ||
      configuredAssetUrl(presentation.desktop_image_key) ||
      configuredAssetUrl(context.commerce.product_image_key_desktop) ||
      context.commerce.product_image_url_desktop || universalImageUrl;
    var mobileImageUrl = presentation.mobile_image_url ||
      configuredAssetUrl(presentation.mobile_image_key) ||
      configuredAssetUrl(context.commerce.product_image_key_mobile) ||
      context.commerce.product_image_url_mobile || universalImageUrl || desktopImageUrl;
    if (presentation.image_type === 'product' && context.commerce.source_product_image_url) {
      desktopImageUrl = context.commerce.source_product_image_url;
      mobileImageUrl = context.commerce.source_product_image_url;
    }
    if (desktopImageUrl || mobileImageUrl) {
      contextImageUrls = {
        desktop: desktopImageUrl || mobileImageUrl,
        mobile: mobileImageUrl || desktopImageUrl
      };
      image.alt = context.content.image_alt || '';
      image.closest('.lplcp__visual').removeAttribute('aria-hidden');
      applyContextImagePosition(false);
      if (isReviewMode()) loadContextImageNow();
    }
    var input = root.querySelector('[data-lplcp-email]');
    input.placeholder = context.content.field_placeholder || '';
    var submit = root.querySelector('[data-lplcp-submit]');
    submit.textContent = context.content.cta_label || '';
    var productLink = root.querySelector('[data-lplcp-product-link]');
    var discountCode = String(context.commerce.discount_code || '').trim();
    var codeWrap = root.querySelector('[data-lplcp-code-wrap]');
    var codeValue = root.querySelector('[data-lplcp-code]');
    if (discountCode) {
      codeValue.textContent = discountCode;
      productLink.href = safeDiscountUrl(discountCode, context.commerce.product_url);
    } else {
      codeWrap.hidden = true;
      productLink.href = safeProductUrl(context.commerce.product_url);
    }
    root.dataset.lplcpContext = context.context_id;
  }

  function bind() {
    root.querySelectorAll('[data-lplcp-close]').forEach(function (button) {
      button.addEventListener('click', function () { closePopup('button'); });
    });
    document.addEventListener('keydown', onKeydown);
    bindResponsiveImageSelection();
    root.querySelector('[data-lplcp-product-link]').addEventListener('click', function () {
      emit('product_clicked');
    });
    root.querySelector('[data-lplcp-copy]').addEventListener('click', copyDiscountCode);
    root.querySelector('[data-lplcp-form]').addEventListener('submit', async function (event) {
      event.preventDefault();
      var form = event.currentTarget;
      var honeypot = root.querySelector('[data-lplcp-honeypot]');
      if (honeypot && String(honeypot.value || '').trim()) {
        emit('bot_blocked', { suppression_reason: 'honeypot_filled' });
        return;
      }
      var email = root.querySelector('[data-lplcp-email]').value.trim();
      var error = root.querySelector('[data-lplcp-error]');
      var submit = root.querySelector('[data-lplcp-submit]');
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }
      emit('submit_attempted', {
        submission_state: isLiveTransport() ? 'live_request_started' : 'mock_request_started'
      });
      error.hidden = true;
      submit.disabled = true;
      submit.textContent = context.content.busy_label;
      try {
        if (isLiveTransport()) {
          await submitToKlaviyo(email);
        } else {
          await new Promise(function (resolve) { window.setTimeout(resolve, 260); });
        }
        showSuccess();
      } catch (requestError) {
        console.warn('[LPL contextual popup] submission failed', requestError.message);
        emit('submit_failed', submissionFailureDetail(requestError));
        error.textContent = context.content.error_message;
        error.hidden = false;
      } finally {
        submit.disabled = false;
        submit.textContent = context.content.cta_label;
      }
    });
  }

  async function init() {
    config = parseConfig();
    root = document.getElementById(ROOT_ID);
    if (!config || !root || !config.context) return;
    context = config.context;
    if (normalizedPathname() !== context.match.pathname) return;
    if (!isReviewMode() && config.pilot.production_enabled !== true) return;
    applyVariants();
    if (!isReviewMode() && !productionIsEnabled()) return;
    impressionId = createRandomId('impression');
    var decision = await suppressionDecision();
    if (!decision.allowed) {
      emit('suppressed', { suppression_reason: decision.reason });
      return;
    }
    if (identityEligibilityRevoked) return;
    populate();
    bind();
    registerReviewApi();
    emit('exposed', decision.instrumentation
      ? { suppression_reason: decision.instrumentation }
      : {});
    if (identityEligibilityRevoked) return;
    if (isReviewMode()) {
      scheduleReviewTrigger();
    } else {
      scheduleProductionTriggers();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
